import type { ScannerHandle, StartScannerOptions } from './types'

export async function startNativeScanner({
    videoElement,
    cameraId,
    onDetected
}: StartScannerOptions): Promise<ScannerHandle> {
    if (!('BarcodeDetector' in window)) {
        throw new Error('BarcodeDetector not supported')
    }

    const supportedFormats = await BarcodeDetector.getSupportedFormats()
    const detector = new BarcodeDetector({ formats: supportedFormats })

    const constraints: MediaStreamConstraints = {
        video: cameraId && cameraId !== '' && cameraId !== '-' ? { deviceId: cameraId } : { facingMode: 'environment' }
    }

    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    videoElement.srcObject = stream

    let stopped = false

    await new Promise<void>(resolve => {
        videoElement.addEventListener('loadedmetadata', () => resolve(), { once: true })
    })

    async function detectLoop() {
        if (stopped) return
        if (videoElement.readyState === videoElement.HAVE_ENOUGH_DATA) {
            try {
                const barcodes = await detector.detect(videoElement)
                if (barcodes.length > 0) {
                    const cx = videoElement.videoWidth / 2
                    const cy = videoElement.videoHeight / 2
                    let best: { dist: number; code: string } | null = null
                    for (const b of barcodes) {
                        const ax = b.cornerPoints.reduce((s, p) => s + p.x, 0) / b.cornerPoints.length
                        const ay = b.cornerPoints.reduce((s, p) => s + p.y, 0) / b.cornerPoints.length
                        const dist = Math.hypot(ax - cx, ay - cy)
                        if (!best || dist < best.dist) best = { dist, code: b.rawValue }
                    }
                    if (best) onDetected(best.code)
                }
            } catch {
                // ignore frame errors
            }
        }
        if (!stopped) {
            window.setTimeout(() => requestAnimationFrame(detectLoop), 200)
        }
    }
    void detectLoop()

    return {
        stop: () => {
            stopped = true
            stream.getTracks().forEach(t => t.stop())
            videoElement.srcObject = null
        }
    }
}
